import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { CanvasTextAlign } from '@/src/renderer/lib/db/models'
import { canvasTextAnchorShift } from '../utils/canvasText'
import { OPTIONS_BAR_REPOSITION_EVENT } from '../optionsBar/CanvasOptionsBar.types'

interface UseMeasuredTextSizeOptions {
  noteId: string
  /** Lage und Maße, wie sie in der Datenbank stehen — die Schwelle, gegen die gemessen wird. */
  stored: { x: number; width: number; height: number }
  /** Welche Kante beim Wachsen stehen bleibt (`canvasTextAnchorShift`). */
  align: CanvasTextAlign | null
  /** `false`, solange an einer Ecke gezogen wird — dann gehört die Größe dem Zeiger. */
  enabled: boolean
  /** Der Zoom des Canvas — die **Options-Leiste** steht in Bildschirm-Pixeln, der Text in Canvas-Einheiten. */
  getZoom?: () => number
}

/** Wie lange die Messungen ruhen müssen, bevor geschrieben wird (wie `useMeasuredNoteHeight`). */
const SETTLE_MS = 200

/** Ab welcher Abweichung geschrieben wird — der Riegel gegen die Rückkopplung (wie `useMeasuredNoteHeight`). */
const SIZE_EPSILON = 1

/** Die Verschiebung, mit der ein Text seine Kante hält, bis die neue Lage geschrieben ist. */
const SHIFT_VAR = '--text-anchor-shift'

/**
 * Die **Options-Leiste** des Texts (React Flows `NodeToolbar`, portaliert):
 * Sie zentriert sich über der gemessenen Hülle, und die ist bis zum Schreiben
 * noch nicht gerückt — ohne dieselbe Verschiebung spränge sie zur Seite und
 * 200 ms später zurück (gemeldet 2026-09-23). `translate` addiert sich zu
 * ihrem `transform`, das React Flow setzt.
 */
function toolbarOf(noteId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`.react-flow__node-toolbar[data-id="${CSS.escape(noteId)}"]`)
}

/**
 * Breite UND Höhe eines **Texts** messen und zurückschreiben — beim Text folgen
 * beide dem Inhalt (CONTEXT.md, Text). Dieselbe Bauart und dieselbe Begründung
 * wie `useMeasuredNoteHeight`: Die Zahl steht in der Datenbank, weil Einrasten,
 * Bereiche und Auswahl mit ihr rechnen; sie ist eine Messung DIESES Geräts und
 * geht deshalb an Sync und Rückgängig vorbei (`setCanvasNoteMeasuredSize`).
 *
 * **Die Ausrichtung hält ihre Kante.** Zentriert wächst der Text nach beiden
 * Seiten, rechtsbündig nach links — dafür rückt `x` mit der Breite. Weil erst
 * nach einer Pause geschrieben wird, schiebt bis dahin `--text-anchor-shift`
 * den Text an die Stelle, an die er gehört; sonst wüchse er erst nach rechts
 * und spränge dann zurück. Da die Verschiebung allein aus der Breite folgt,
 * nimmt ein ⌘Z am Inhalt sie von selbst mit zurück.
 *
 * Gemessen wird `offsetWidth`/`offsetHeight` — die kennen keine
 * Transformation, die **Neigung** ändert also nichts an den Maßen.
 */
export function useMeasuredTextSize({
  noteId,
  stored,
  align,
  enabled,
  getZoom,
}: UseMeasuredTextSizeOptions): (element: HTMLElement | null) => void {
  const dataSource = useDataSource()
  const [element, setElement] = useState<HTMLElement | null>(null)
  const storedRef = useRef(stored)
  storedRef.current = stored
  const alignRef = useRef(align)
  alignRef.current = align
  const getZoomRef = useRef(getZoom)
  getZoomRef.current = getZoom

  const showShift = useCallback((target: HTMLElement, shift: number) => {
    const toolbar = toolbarOf(noteId)
    if (shift) target.style.setProperty(SHIFT_VAR, `${shift}px`)
    else target.style.removeProperty(SHIFT_VAR)
    if (!toolbar) return
    const translate = shift ? `${shift * (getZoomRef.current?.() ?? 1)}px 0px` : ''
    // Verglichen mit dem, was hier zuletzt gesetzt wurde — der Browser liest
    // `translate` in eigener Schreibweise zurück („-40px" statt „-40px 0px").
    if ((toolbar.dataset.anchorShift ?? '') === translate) return
    toolbar.dataset.anchorShift = translate
    toolbar.style.translate = translate
    // Die Leiste weicht der Werkzeug-Pille aus — sie muss an der neuen Stelle neu messen.
    toolbar.dispatchEvent(new Event(OPTIONS_BAR_REPOSITION_EVENT))
  }, [noteId])
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const measureRef = useCallback((next: HTMLElement | null) => {
    setElement(next)
  }, [])

  const commit = useCallback(() => {
    if (!element) return
    const width = element.offsetWidth
    const height = element.offsetHeight
    // Eine Null kommt aus einem Tab hinter `display: none` (CLAUDE.md, TabStack-Falle).
    if (width <= 0 || height <= 0) return
    const { x, width: storedWidth, height: storedHeight } = storedRef.current
    if (Math.abs(width - storedWidth) < SIZE_EPSILON && Math.abs(height - storedHeight) < SIZE_EPSILON) return
    const shift = canvasTextAnchorShift(alignRef.current, width - storedWidth)
    void dataSource.setCanvasNoteMeasuredSize(noteId, { width, height, ...(shift ? { x: x + shift } : {}) })
  }, [element, noteId, dataSource])

  /** Die Verschiebung zur gespeicherten Lage zeigen und das Schreiben vormerken. */
  const follow = useCallback(() => {
    if (!element) return
    const width = element.offsetWidth
    if (width <= 0) return
    showShift(element, canvasTextAnchorShift(alignRef.current, width - storedRef.current.width))
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(commit, SETTLE_MS)
  }, [element, commit, showShift])

  useEffect(() => {
    if (!enabled || !element) return
    // Der ResizeObserver meldet nach dem Layout, vor dem Zeichnen — die
    // Verschiebung steht also im selben Bild wie das Wachstum.
    const observer = new ResizeObserver(follow)
    observer.observe(element)
    return () => {
      clearTimeout(timerRef.current)
      observer.disconnect()
    }
  }, [enabled, element, follow])

  /*
    Kommt die neue Lage an (oder eine andere Ausrichtung), steht die
    Verschiebung im selben Bild wieder auf ihrem Rest — der Knoten ist
    gerückt, der Text darf es nicht noch einmal.
  */
  useLayoutEffect(() => {
    if (!element) return
    if (!enabled) {
      showShift(element, 0)
      return
    }
    follow()
  }, [enabled, element, follow, showShift, stored.x, stored.width, align])

  return measureRef
}
